import { supabase } from './supabase';

export interface ViewportState {
    x: number;
    y: number;
    scale: number;
}

// Lookbook state that gets saved per-project
export interface LookbookState {
    layout_data: any; // LayoutItem[][] structure
    image_ids: string[];
    generations: any[]; // Full generation objects with positions
    viewport?: ViewportState;
    galleryScale?: number; // Gallery zoom level (1-5)
    saved_at?: string;
}

// Helper to filter out invalid/broken generations (missing URLs, failed loads, etc.)
const filterValidGenerations = (generations: any[]): any[] => {
    if (!generations || !Array.isArray(generations)) return [];

    return generations.filter(gen => {
        // Text items don't need image URLs
        if (gen.type === 'text') return true;

        // Filter out items still in loading state
        if (gen.isLoading) return false;

        // Must have a valid image URL
        const imgUrl = gen.img || gen.src;
        if (!imgUrl || typeof imgUrl !== 'string') return false;

        // Filter out placeholder/loading states
        if (imgUrl === '' || imgUrl === 'loading' || imgUrl === 'pending') return false;

        // Filter out data URLs that are too short (likely broken)
        if (imgUrl.startsWith('data:') && imgUrl.length < 100) return false;

        return true;
    });
};

// Legacy Session interface - kept for migration purposes
export interface Session {
    id: string;
    user_id: string;
    name: string;
    layout_data: any; // LayoutItem[][] structure
    viewport?: ViewportState; // New viewport state
    image_ids: string[];
    thumbnail_url?: string;
    created_at: string;
    updated_at: string;
}

export interface SessionInput {
    name: string;
    layout_data: any;
    viewport?: ViewportState;
    image_ids: string[];
    thumbnail_url?: string;
}

const TEMP_SESSION_KEY = 'ezart_temp_session';
const ACTIVE_PROJECT_KEY = 'ezart_active_lookbook_project';

// Temporary session (localStorage) for refresh persistence
export const saveTempSession = (layoutData: any, imageIds: string[], generations: any[], viewport?: ViewportState) => {
    try {
        const tempSession = {
            layout_data: layoutData,
            image_ids: imageIds,
            generations: generations, // Save full generation data for restoration
            viewport: viewport,
            saved_at: new Date().toISOString()
        };
        localStorage.setItem(TEMP_SESSION_KEY, JSON.stringify(tempSession));
    } catch (e) {
        console.error('Failed to save temp session:', e);
    }
};

export const loadTempSession = () => {
    try {
        const data = localStorage.getItem(TEMP_SESSION_KEY);
        if (!data) return null;
        const parsed = JSON.parse(data);

        // Filter out invalid generations
        if (parsed?.generations) {
            parsed.generations = filterValidGenerations(parsed.generations);
        }

        return parsed;
    } catch (e) {
        console.error('Failed to load temp session:', e);
        return null;
    }
};

export const clearTempSession = () => {
    try {
        localStorage.removeItem(TEMP_SESSION_KEY);
    } catch (e) {
        console.error('Failed to clear temp session:', e);
    }
};

// Database sessions
export const fetchSessions = async (): Promise<Session[]> => {
    const { data, error } = await supabase
        .from('sessions')
        .select('*')
        .order('updated_at', { ascending: false });

    if (error) {
        console.error('Error fetching sessions:', error);
        return [];
    }
    return data || [];
};

export const fetchSession = async (id: string): Promise<Session | null> => {
    const { data, error } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', id)
        .single();

    if (error) {
        console.error('Error fetching session:', error);
        return null;
    }
    return data;
};

export const createSession = async (input: SessionInput): Promise<Session | null> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
        .from('sessions')
        .insert({
            user_id: user.id,
            ...input
        })
        .select()
        .single();

    if (error) {
        console.error('Error creating session:', error);
        return null;
    }
    return data;
};

export const updateSession = async (id: string, input: Partial<SessionInput>): Promise<Session | null> => {
    const { data, error } = await supabase
        .from('sessions')
        .update({
            ...input,
            updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

    if (error) {
        console.error('Error updating session:', error);
        return null;
    }
    return data;
};

export const deleteSession = async (id: string): Promise<boolean> => {
    const { error } = await supabase
        .from('sessions')
        .delete()
        .eq('id', id);

    if (error) {
        console.error('Error deleting session:', error);
        return false;
    }
    return true;
};

export const getMostRecentSession = async (): Promise<Session | null> => {
    const { data, error } = await supabase
        .from('sessions')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1)
        .single();

    if (error) {
        // No sessions exist yet
        return null;
    }
    return data;
};

// ============================================
// PROJECT-BASED LOOKBOOK STATE MANAGEMENT
// ============================================

// Get/Set active lookbook project ID in localStorage
export const getActiveProjectId = (): string | null => {
    try {
        return localStorage.getItem(ACTIVE_PROJECT_KEY);
    } catch (e) {
        return null;
    }
};

export const setActiveProjectId = (projectId: string | null) => {
    try {
        if (projectId) {
            localStorage.setItem(ACTIVE_PROJECT_KEY, projectId);
        } else {
            localStorage.removeItem(ACTIVE_PROJECT_KEY);
        }
    } catch (e) {
        console.error('Failed to set active project ID:', e);
    }
};

// Save lookbook state to a specific project
export const saveProjectLookbookState = async (
    projectId: string,
    state: LookbookState
): Promise<boolean> => {
    try {
        const { error } = await supabase
            .from('projects')
            .update({
                lookbook_state: {
                    ...state,
                    saved_at: new Date().toISOString()
                },
                updated_at: new Date().toISOString()
            })
            .eq('id', projectId);

        if (error) {
            console.error('Error saving lookbook state to project:', error);
            return false;
        }
        return true;
    } catch (e) {
        console.error('Failed to save lookbook state:', e);
        return false;
    }
};

// Load lookbook state from a specific project
export const loadProjectLookbookState = async (
    projectId: string
): Promise<LookbookState | null> => {
    try {
        const { data, error } = await supabase
            .from('projects')
            .select('lookbook_state')
            .eq('id', projectId)
            .single();

        if (error) {
            console.error('Error loading lookbook state from project:', error);
            return null;
        }

        const state = data?.lookbook_state;
        if (!state) return null;

        // Filter out invalid generations
        if (state.generations) {
            state.generations = filterValidGenerations(state.generations);
        }

        return state;
    } catch (e) {
        console.error('Failed to load lookbook state:', e);
        return null;
    }
};

// Clear lookbook state from a project
export const clearProjectLookbookState = async (projectId: string): Promise<boolean> => {
    try {
        const { error } = await supabase
            .from('projects')
            .update({
                lookbook_state: null,
                updated_at: new Date().toISOString()
            })
            .eq('id', projectId);

        if (error) {
            console.error('Error clearing lookbook state from project:', error);
            return false;
        }
        return true;
    } catch (e) {
        console.error('Failed to clear lookbook state:', e);
        return false;
    }
};

// Get project with lookbook state
export const getProjectWithLookbookState = async (projectId: string) => {
    try {
        const { data, error } = await supabase
            .from('projects')
            .select('id, title, lookbook_state, updated_at')
            .eq('id', projectId)
            .single();

        if (error) {
            console.error('Error fetching project with lookbook state:', error);
            return null;
        }

        return data;
    } catch (e) {
        console.error('Failed to fetch project:', e);
        return null;
    }
};

// Get all projects (for project selector)
export const getProjectsForLookbook = async () => {
    try {
        const { data, error } = await supabase
            .from('projects')
            .select('id, title, lookbook_state, updated_at')
            .order('updated_at', { ascending: false });

        if (error) {
            console.error('Error fetching projects for lookbook:', error);
            return [];
        }

        return data || [];
    } catch (e) {
        console.error('Failed to fetch projects:', e);
        return [];
    }
};
